import { useState, useRef, useEffect, useCallback, memo } from "react"
import { useLocation, Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import type { DbStartAtIndex } from "@/db/schema"
import {
  db,
  ObjectType,
  ObjectStatus,
  StartAtType,
} from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import {
  updateTaskTitle,
  deleteTask,
  convertTaskToSection,
  checklistParentPath,
  queryChecklistItems,
} from "@/db/operations"
import { checklistProgress } from "@totodo/operations"
import { TaskCheckbox } from "@/features/tasks/components/TaskCheckbox"
import { StatusSelector } from "@/features/tasks/components/StatusSelector"
import { UserStatusDot } from "@/shared/components/UserStatusDot"
import { hasMeaningfulData, isNewlyCreated } from "../utils/hasMeaningfulData"
import { useAutoSave } from "../hooks/useAutoSave"
import { useEditFocusStore } from "../stores/editFocusStore"
import { useSettingBoolean } from "@/features/settings/hooks/useSetting"

/** Sections are only valid inside container views (project / list / area). */
const CONTAINER_ROUTE_RE = /^\/(project|list|area)\//

interface DetailHeaderProps {
  task: DbObject
  startAtData?: DbStartAtIndex
  /** Whether to auto-focus the title for editing on mount */
  editOnMount?: boolean
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  /** Called when the task detail should close (Enter confirms title) */
  onClose?: () => void
  /** Called when the task is deleted via Backspace-on-empty */
  onDelete?: () => void
}

/**
 * Task detail header — checkbox + editable title.
 *
 * Uses `contentEditable` div for inline title editing (v1 pattern).
 * Saves via `updateTaskTitle()` on blur.
 */
export const DetailHeader = memo(function DetailHeader({
  task,
  startAtData,
  editOnMount,
  onComplete,
  onUncomplete,
  onClose,
  onDelete,
}: DetailHeaderProps) {
  const { pathname } = useLocation()
  const isContainerView = CONTAINER_ROUTE_RE.test(pathname)
  const titleRef = useRef<HTMLDivElement>(null)
  const [, setIsEditing] = useState(false)
  const [showStatus, setShowStatus] = useState(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isCompleted = task.status === ObjectStatus.Completed
  const isCanceled = task.status === ObjectStatus.Canceled
  const isSomeday = startAtData?.type === StartAtType.Someday
  const isContainer =
    task.type === ObjectType.Project || task.type === ObjectType.List
  const { t } = useTranslation()
  const { value: isCheckboxProgress } = useSettingBoolean("#checkbox-progress")

  // Progress data for Project/List types (§43: TimelineIcon in header)
  const [progressData] = useLiveQueryImmediate(
    async () => {
      const taskId = task.id
      if (!taskId) return null

      // Project/List: count child tasks
      if (isContainer) {
        const children = await db.totodo
          .where("[parentPath+isDeleted]")
          .equals([`root/${taskId}`, 0])
          .filter((c) => c.type === ObjectType.Task)
          .toArray()
        const total = children.length
        const completed = children.filter(
          (c) =>
            c.status === ObjectStatus.Completed ||
            c.status === ObjectStatus.Canceled,
        ).length
        return {
          percentage: total > 0 ? (completed / total) * 100 : 0,
          unfinished: total - completed,
          checklistProgress: null as {
            completed: number
            total: number
          } | null,
        }
      }

      // Regular task: count checklist items — the shared rule get_task_detail reports too
      if (task.type === ObjectType.Task) {
        const rows = await queryChecklistItems(
          checklistParentPath(task.parentPath, taskId),
          db,
        )
        const { completed, total } = checklistProgress(rows)
        if (total === 0) return null
        return {
          percentage: 0,
          unfinished: 0,
          checklistProgress: { completed, total },
        }
      }

      return null
    },
    [
      "detail-header-progress",
      task.id,
      isContainer,
      task.type,
      task.parentPath,
    ],
    null,
  )

  // Auto-save via reusable hook
  const {
    scheduleAutoSave,
    saveNow,
    cancel: cancelAutoSave,
  } = useAutoSave<string>({
    value: task.title,
    saveFn: (newTitle) => {
      if (task.id && newTitle && newTitle !== task.title) {
        updateTaskTitle(task.id, newTitle)
      }
    },
  })

  // Cleanup auto-save on unmount (hook handles its own cleanup)

  // Auto-focus title when editFocusStore signals this item, or legacy editOnMount prop
  const consumeEditFocus = useEditFocusStore((s) => s.consumeEditFocus)
  useEffect(() => {
    const shouldFocus =
      editOnMount || (task.id ? consumeEditFocus(task.id) : false)
    if (shouldFocus && titleRef.current) {
      // preventScroll — TaskItem's scrollOpenIntoView positions this card.
      // The browser's own focus-scroll overshoots (measured ~240px on the
      // checklist rows) and runs before our animation, so the two fought over
      // the very card being opened. See dreambase/ui/scroll-into-view.md.
      titleRef.current.focus({ preventScroll: true })
      // Move cursor to end
      const range = document.createRange()
      const sel = window.getSelection()
      range.selectNodeContents(titleRef.current)
      range.collapse(false)
      sel?.removeAllRanges()
      sel?.addRange(range)
      setIsEditing(true)
    }
  }, [editOnMount, task.id, consumeEditFocus])

  const handleToggle = useCallback(() => {
    const id = task.id
    if (!id) return
    if (isCompleted || isCanceled) {
      onUncomplete(id)
    } else {
      onComplete(id)
    }
  }, [task.id, isCompleted, isCanceled, onComplete, onUncomplete])

  // Long-press on checkbox area → open StatusSelector
  const handleCheckboxPointerDown = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      setShowStatus(true)
    }, 500) // 500ms long-press
  }, [])

  const handleCheckboxPointerUp = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const handleCheckboxPointerLeave = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const handleBlur = useCallback(() => {
    setIsEditing(false)
    const id = task.id
    if (!id || !titleRef.current) return
    const newTitle = titleRef.current.textContent?.trim() ?? ""
    if (newTitle && newTitle !== task.title) {
      saveNow(newTitle)
    } else if (!newTitle) {
      cancelAutoSave()
      // Restore original title if emptied
      titleRef.current.textContent = task.title
    } else {
      cancelAutoSave()
    }
  }, [task.id, task.title, saveNow, cancelAutoSave])

  /** Auto-save on each input keystroke (debounced). */
  const handleInput = useCallback(() => {
    // Detect "# " at start → convert task to section (v1 pattern).
    // Only allowed in container views (project/list/area) where sections
    // are visible.  In date-driven views the user would lose sight of the
    // item, so we silently ignore the shortcut there.
    const text = titleRef.current?.textContent ?? ""
    if (text === "# " && task.id && isContainerView) {
      convertTaskToSection(task.id)
      onDelete?.() // Close the detail view — section editing happens inline in container
      return
    }

    const newTitle = titleRef.current?.textContent?.trim() ?? ""
    if (newTitle) {
      scheduleAutoSave(newTitle)
    }
  }, [task.id, task.title, onDelete, isContainerView, scheduleAutoSave])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault()
        titleRef.current?.blur()
        onClose?.()
      }
      if (e.key === "Escape") {
        e.preventDefault()
        if (titleRef.current) {
          titleRef.current.textContent = task.title
        }
        titleRef.current?.blur()
      }
      // Backspace on empty title — delete task if newly created + no meaningful data
      if (e.key === "Backspace") {
        const content = titleRef.current?.textContent?.trim() ?? ""
        if (content === "" && task.id && isNewlyCreated(task)) {
          hasMeaningfulData(task).then((hasMeaningful) => {
            if (!hasMeaningful && task.id) {
              e.preventDefault()
              deleteTask(task.id)
              onDelete?.()
            }
          })
        }
      }
    },
    [task.title, task.id, task.createdAt, onClose, onDelete],
  )

  return (
    <div>
      <div className="flex items-start gap-3 px-4 pt-4 pb-1">
        {/* Checkbox — tap to toggle, long-press for status selector */}
        <div
          className="mt-0.5"
          onPointerDown={handleCheckboxPointerDown}
          onPointerUp={handleCheckboxPointerUp}
          onPointerLeave={handleCheckboxPointerLeave}
        >
          <TaskCheckbox
            status={task.status}
            isSomeday={isSomeday}
            size="medium"
            taskType={task.type}
            percentage={progressData?.percentage ?? 0}
            unfinished={progressData?.unfinished ?? 0}
            checklistProgress={
              isCheckboxProgress
                ? (progressData?.checklistProgress ?? null)
                : null
            }
            onToggle={handleToggle}
          />
        </div>

        {/* UserStatus dot — §21: next to title in detail view */}
        {task.id &&
          task.userStatus != null &&
          task.userStatus !== 0 &&
          task.userStatus !== "" && (
            <div className="mt-1">
              <UserStatusDot userStatus={task.userStatus} taskId={task.id} />
            </div>
          )}

        {/* Editable title */}
        <div
          ref={titleRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={t("Task title")}
          className={cn(
            "ce-placeholder min-w-0 flex-1 text-base font-medium outline-none",
            "rounded px-1 -mx-1",
            (isCompleted || isCanceled) && "text-muted-foreground",
            isCanceled && "line-through",
          )}
          data-placeholder={t("New To To-Do")}
          onFocus={() => setIsEditing(true)}
          onBlur={handleBlur}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
        >
          {task.title}
        </div>

        {/* Open container navigation button — §43: for Project/List types */}
        {isContainer && task.id && (
          <Link
            to={`/${task.type === ObjectType.Project ? "project" : "list"}/${task.id}`}
            className="mt-0.5 shrink-0 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title={t("Open {{type}}", {
              type: task.type === ObjectType.Project ? t("project") : t("list"),
            })}
            aria-label={t("Open {{type}}", {
              type: task.type === ObjectType.Project ? t("project") : t("list"),
            })}
          >
            <svg
              aria-hidden="true"
              width={16}
              height={16}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
          </Link>
        )}
      </div>

      {/* StatusSelector — shown on long-press */}
      {showStatus && task.id && (
        <div className="px-4 py-1">
          <StatusSelector
            taskId={task.id}
            currentStatus={task.status}
            onClose={() => setShowStatus(false)}
          />
        </div>
      )}
    </div>
  )
})
